import { useEffect, useMemo, useRef, useState } from "react";
import { isEmbedded, postToHost, subscribeToHost } from "@/lib/embed-bridge";
import type { HostToIframe } from "@/lib/embed-protocol";

/**
 * React hook exposing the embed-side postMessage bridge.
 *
 * Usage:
 *   const { embedded, mode, onHostMessage, sendReady, sendResize, ... } = useEmbedBridge()
 *
 * The hook:
 *   1. Detects whether we're inside an iframe
 *   2. Reads the embed mode from the `embed=floating|inline` URL param
 *   3. Wires listeners for host→iframe messages via `onHostMessage`
 *   4. Exposes helpers for iframe→host notifications
 */

type HostHandler = (msg: HostToIframe) => void;

export function useEmbedBridge() {
  const embedded = isEmbedded();
  const [gaggleToken, setGaggleToken] = useState<string | null>(null);
  const [theme, setTheme] = useState<"light" | "dark" | null>(null);
  const [opened, setOpened] = useState(false);

  // Host-message subscribers registered by feature code (e.g. chat page).
  const extraHandlersRef = useRef<Set<HostHandler>>(new Set());

  const mode = useMemo<"floating" | "inline" | null>(() => {
    if (!embedded) return null;
    const params = new URLSearchParams(window.location.search);
    const embed = params.get("embed");
    if (embed === "inline") return "inline";
    if (embed === "floating") return "floating";
    return null;
  }, [embedded]);

  const districtUuid = useMemo<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("d");
  }, []);

  useEffect(() => {
    if (!embedded) return;
    const unsub = subscribeToHost((msg) => {
      switch (msg.type) {
        case "reachout:set-user":
          setGaggleToken(msg.gaggleToken);
          break;
        case "reachout:set-theme":
          setTheme(msg.theme);
          break;
        case "reachout:open":
          setOpened(true);
          break;
        case "reachout:close":
          setOpened(false);
          break;
        case "reachout:navigate":
          // Consumers can hook via extraHandlersRef
          break;
      }
      for (const h of extraHandlersRef.current) h(msg);
    });
    return unsub;
  }, [embedded]);

  // Auto-emit `ready` once on mount when embedded.
  useEffect(() => {
    if (!embedded) return;
    postToHost({
      type: "reachout:ready",
      chatOrigin: window.location.origin,
    });
  }, [embedded]);

  // Auto-emit `resize` on document body size changes (useful for inline mode).
  useEffect(() => {
    if (!embedded || mode !== "inline") return;
    const emit = () => {
      postToHost({
        type: "reachout:resize",
        height: document.documentElement.scrollHeight,
      });
    };
    emit();
    const ro = new ResizeObserver(emit);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [embedded, mode]);

  return {
    embedded,
    mode,
    districtUuid,
    gaggleToken,
    theme,
    opened,
    sendMessageSent: () =>
      postToHost({
        type: "reachout:message-sent",
        timestamp: new Date().toISOString(),
      }),
    sendSessionEnded: (reason: "user-closed" | "agent-ended" | "timeout") =>
      postToHost({ type: "reachout:session-ended", reason }),
    onHostMessage: (handler: HostHandler): (() => void) => {
      extraHandlersRef.current.add(handler);
      return () => {
        extraHandlersRef.current.delete(handler);
      };
    },
  };
}
